import React, { useState, useEffect, useMemo, useContext } from "react";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, startOfDay, isBefore } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { callProxy } from "../api/api";
import { TokenContext } from "../components/TokenContext";

export default function ApartmentAvailabilityCalendar({ apartmentId, apartment, onChange }) {
  const { token } = useContext(TokenContext);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [priceMap, setPriceMap] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = startOfDay(new Date());
  const [blockedDates, setBlockedDates] = useState(new Set());

useEffect(() => {
    if (!apartmentId) return;

    const loadRates = async () => {
      try {
        // Get iCal URL from passed apartment prop
        const icalUrl = apartment?.ical_url;
        console.log("🔹 iCal URL:", icalUrl);

        // Fetch rates from API
        const res = await callProxy(`/api/rates/${apartmentId}`, token);
        const apartmentRates = res?.data?.data[String(apartmentId)] || {};

        const map = {};
        const blocked = new Set();

        Object.entries(apartmentRates).forEach(([date, info]) => {
          if (date === "ical_url") return;
          const price = info?.price ?? info?.dailyPrice ?? info?.price?.total ?? 0;
          map[date] = { price: Number(price), available: info?.available !== false };
          if (info?.available === false) blocked.add(date);
        });

        setPriceMap(map);
        setBlockedDates(blocked);

        // Fetch iCal events
        if (icalUrl) {
          const icalRes = await fetch(icalUrl);
          const icsText = await icalRes.text();
          const events = icsText.split("BEGIN:VEVENT").slice(1);
          const newBlocked = new Set(blocked);

          events.forEach(event => {
            const startLine = event.match(/DTSTART(?:;VALUE=DATE)?:([\dT]+)/);
            const endLine = event.match(/DTEND(?:;VALUE=DATE)?:([\dT]+)/);
            if (startLine && endLine) {
              const parseDate = str => `${str.slice(0,4)}-${str.slice(4,6)}-${str.slice(6,8)}`;
              const start = new Date(parseDate(startLine[1]));
              const end = new Date(parseDate(endLine[1]));
              for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
                newBlocked.add(format(d, "yyyy-MM-dd"));
              }
            }
          });

          setBlockedDates(newBlocked);
        }

      } catch (err) {
        console.error("Rates/iCal fetch failed:", err);
      }
    };

    loadRates();
  }, [apartmentId, apartment, token]);


  const handleDayClick = (dateStr) => {
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(dateStr);
      setCheckOut("");
    } else {
      if (dateStr < checkIn) setCheckIn(dateStr);
      else {
        setCheckOut(dateStr);
        onChange({ checkIn: parseISO(checkIn), checkOut: parseISO(dateStr), available: true });
      }
    }
  };

  const daysOfMonth = useMemo(() => eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) }), [currentMonth]);

  const lowestPrice = useMemo(() => {
    const prices = daysOfMonth
      .map(date => {
        const dateStr = format(date, "yyyy-MM-dd");
        return priceMap[dateStr]?.price;
      })
      .filter(p => typeof p === "number" && p > 0);
    return prices.length ? Math.min(...prices) : null;
  }, [daysOfMonth, priceMap]);

  return (
    <div className="p-2 select-none">
      <div className="flex justify-between items-center mb-4 px-2">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}><ChevronLeft size={18}/></button>
        <div className="flex flex-col items-center">
          <span className="font-bold text-sm">{format(currentMonth, "MMMM yyyy")}</span>
          {lowestPrice && <span className="text-[11px] text-emerald-600 font-semibold">From {lowestPrice} AED</span>}
        </div>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><ChevronRight size={18}/></button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
          <div key={d} className="text-[10px] text-center font-bold text-slate-400">{d}</div>
        ))}
        {daysOfMonth.map(date => {
          const dateStr = format(date, "yyyy-MM-dd");
          const dayData = priceMap[dateStr];
          const isPast = isBefore(date, today);
          const isBooked = blockedDates.has(dateStr);
          const isDisabled = isPast || isBooked;
          const price = dayData?.price || null;

          const isSelected = dateStr === checkIn || dateStr === checkOut;
          const inRange = checkIn && checkOut && dateStr > checkIn && dateStr < checkOut;

          return (
            <div
              key={dateStr}
              onClick={() => !isDisabled && handleDayClick(dateStr)}
              className={`h-14 flex flex-col items-center justify-center rounded-lg cursor-pointer transition-all text-xs
                ${isDisabled ? "bg-slate-100 text-slate-300 cursor-not-allowed" : "hover:bg-slate-100"}
                ${isSelected ? "bg-[#3a2a00] text-white font-bold" : ""}
                ${inRange ? "bg-[#3a2a00]/10 text-[#3a2a00]" : ""}`}
            >
              <span>{date.getDate()}</span>
              {price ? (
                <span className={`text-[7.5px] ${lowestPrice === price ? "text-emerald-600 font-bold" : "text-slate-400"}`}>{price} AED</span>
              ) : (
                <span className="text-[9px] invisible">0</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
